import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import {
  FileText,
  Download,
  Calendar,
  Filter,
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
} from 'lucide-react';
import PageHeader from '../../../components/common/PageHeader';

const CopyTradingReports = () => {
  const reports = [
    {
      id: 'CRPT001',
      name: 'Monthly Performance Report',
      type: 'performance',
      period: 'January 2024',
      generatedAt: '2024-02-01',
      status: 'ready',
    },
    {
      id: 'CRPT002',
      name: 'Follower Allocation Report',
      type: 'allocation',
      period: 'January 2024',
      generatedAt: '2024-02-01',
      status: 'ready',
    },
    {
      id: 'CRPT003',
      name: 'Commission Summary Report',
      type: 'commission',
      period: 'January 2024',
      generatedAt: '2024-02-01',
      status: 'ready',
    },
    {
      id: 'CRPT004',
      name: 'Trade Analysis Report',
      type: 'trading',
      period: 'January 2024',
      generatedAt: '2024-02-01',
      status: 'processing',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="sticky top-0 z-20 bg-background pt-4">
        <PageHeader
          title="Copy Trading Reports"
          description="Generate and view Copy Trading module reports"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
          <FileText className="h-5 w-5 text-blue-600" />
          <div>
            <p className="text-sm text-muted-foreground">Total Reports</p>
            <p className="text-2xl font-bold text-blue-600">{reports.length}</p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-green-50 rounded-lg">
          <FileText className="h-5 w-5 text-green-600" />
          <div>
            <p className="text-sm text-muted-foreground">Ready Reports</p>
            <p className="text-2xl font-bold text-green-600">
              {reports.filter(r => r.status === 'ready').length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-yellow-50 rounded-lg">
          <FileText className="h-5 w-5 text-yellow-600" />
          <div>
            <p className="text-sm text-muted-foreground">Processing</p>
            <p className="text-2xl font-bold text-yellow-600">
              {reports.filter(r => r.status === 'processing').length}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 p-4 bg-purple-50 rounded-lg">
          <Calendar className="h-5 w-5 text-purple-600" />
          <div>
            <p className="text-sm text-muted-foreground">This Month</p>
            <p className="text-2xl font-bold text-purple-600">
              {reports.length}
            </p>
          </div>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Available Reports</CardTitle>
              <CardDescription>
                Generate and download Copy Trading reports
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm">
                <Calendar className="h-4 w-4 mr-2" />
                Date Range
              </Button>
              <Button variant="outline" size="sm">
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {reports.map(report => (
              <div
                key={report.id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <FileText className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{report.name}</h4>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Badge variant="outline">{report.type}</Badge>
                      <span>•</span>
                      <span>{report.period}</span>
                      <span>•</span>
                      <span>
                        Generated:{' '}
                        {new Date(report.generatedAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    className={
                      report.status === 'ready'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }
                  >
                    {report.status.charAt(0).toUpperCase() +
                      report.status.slice(1)}
                  </Badge>
                  {report.status === 'ready' && (
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Generate New Report</CardTitle>
          <CardDescription>Create custom Copy Trading reports</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="h-24 flex-col gap-2">
              <BarChart3 className="h-6 w-6" />
              <span>Performance Report</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2">
              <Users className="h-6 w-6" />
              <span>Follower Report</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2">
              <DollarSign className="h-6 w-6" />
              <span>Commission Report</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2">
              <TrendingUp className="h-6 w-6" />
              <span>Trade Analysis</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2">
              <FileText className="h-6 w-6" />
              <span>Allocation Report</span>
            </Button>
            <Button variant="outline" className="h-24 flex-col gap-2">
              <Calendar className="h-6 w-6" />
              <span>Custom Report</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CopyTradingReports;
